import React, { useMemo } from "react";
import { useLingui } from "@lingui/react/macro";
import { msg } from "@lingui/core/macro";
import { useRemotePaymentTermsPerCruise } from "features/cancellation-policy/useRemotePaymentTerms";
import { EnhancedAvailablePriceWithFareCode } from "src/ssc-api/prices/types";
import {
  isFullyRefundable,
  isPartiallyRefundable
} from "src/utils/payment-terms-helpers";
import * as styles from "./RefundableLabel.module.scss";
import { Icon } from "@silversea-ssc/ui-components-react";

type Props = {
  priceFrom: EnhancedAvailablePriceWithFareCode | undefined;
  cruiseCode: string;
};

const RefundableLabel: React.FC<Props> = ({ priceFrom, cruiseCode }) => {
  const { t } = useLingui();

  const remotePaymentTerms = useRemotePaymentTermsPerCruise(
    priceFrom ? [priceFrom.webFareCode] : [],
    cruiseCode,
    true
  );

  const depositType =
    remotePaymentTerms.length > 0
      ? remotePaymentTerms[0].data?.depositType
      : null;

  const currentCancellationPeriod =
    remotePaymentTerms.length > 0
      ? remotePaymentTerms[0].data?.currentCancellationPeriod
      : null;

  const refundabilityLabel = useMemo(() => {
    if (
      priceFrom &&
      priceFrom.webFareCode !== "Essential" &&
      currentCancellationPeriod &&
      depositType
    ) {
      if (isFullyRefundable(depositType, currentCancellationPeriod))
        return msg`Fully refundable`;
      else if (isPartiallyRefundable(depositType, currentCancellationPeriod)) {
        return msg`Partially refundable`;
      }
    }
    return null;
  }, [priceFrom, currentCancellationPeriod, depositType]);

  if (refundabilityLabel === null) return null;

  return (
    <div className={styles.container}>
      <Icon name="verified_user" className={styles.icon} padding={false} />
      <div className={styles.label}>{t(refundabilityLabel)}</div>
    </div>
  );
};

export default RefundableLabel;
